import { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { Search, LogOut, Plus, Trash2, Radio, UserPlus, Users, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import {
  useListUsersQuery,
  useCreateUserMutation,
  useDeleteUserMutation,
  useAssignStationMutation,
  useRevokeStationMutation,
  useListStationsQuery,
} from '../../store/api';

interface UserManagementScreenProps {
  currentUsername: string;
  onLogout: () => void;
  onBack: () => void;
}

export function UserManagementScreen({ currentUsername, onLogout, onBack }: UserManagementScreenProps) {
  const [search, setSearch] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newUserId, setNewUserId] = useState('');
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignUserId, setAssignUserId] = useState<number | null>(null);
  const [selectedStationId, setSelectedStationId] = useState<string>('');
  const [deleteConfirm, setDeleteConfirm] = useState<{ userId: number; userid: string } | null>(null);
  const [revokeConfirm, setRevokeConfirm] = useState<{ userId: number; subscriptionId: number; stationName: string } | null>(null);

  const { data: users = [], isLoading } = useListUsersQuery(search || undefined);
  const { data: stations = [] } = useListStationsQuery();
  const [createUser] = useCreateUserMutation();
  const [deleteUser] = useDeleteUserMutation();
  const [assignStation] = useAssignStationMutation();
  const [revokeStation] = useRevokeStationMutation();

  const handleCreate = async () => {
    if (newUserId.length !== 8) {
      toast.error('User ID must be exactly 8 characters');
      return;
    }
    try {
      await createUser({ userid: newUserId }).unwrap();
      toast.success(`User ${newUserId} created`);
      setCreateDialogOpen(false);
      setNewUserId('');
    } catch (err: any) {
      const msg = err?.data?.detail || 'Failed to create user';
      toast.error(msg);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await deleteUser(deleteConfirm.userId).unwrap();
      toast.success(`User ${deleteConfirm.userid} deleted`);
      setDeleteConfirm(null);
    } catch (err: any) {
      toast.error(err?.data?.detail || 'Failed to delete user');
    }
  };

  const handleAssign = async () => {
    if (!assignUserId || !selectedStationId) return;
    try {
      await assignStation({ userId: assignUserId, stationId: Number(selectedStationId) }).unwrap();
      toast.success('Station assigned');
      setAssignDialogOpen(false);
      setAssignUserId(null);
      setSelectedStationId('');
    } catch (err: any) {
      toast.error(err?.data?.detail || 'Failed to assign station');
    }
  };

  const handleRevoke = async () => {
    if (!revokeConfirm) return;
    try {
      await revokeStation({ userId: revokeConfirm.userId, subscriptionId: revokeConfirm.subscriptionId }).unwrap();
      toast.success(`Access to ${revokeConfirm.stationName} revoked`);
      setRevokeConfirm(null);
    } catch (err: any) {
      toast.error(err?.data?.detail || 'Failed to revoke access');
    }
  };

  const activeStations = (stations as any[]).filter((s: any) => !s.isDeleted && s.status !== 'Deleted');

  return (
    <div className="min-h-screen bg-[#0a0a1a] text-white">
      {/* Header */}
      <header className="border-b border-gray-800 bg-[#0d0d24] px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={onBack} className="text-gray-400 hover:text-white">
              <ArrowLeft className="size-4 mr-1" /> Back
            </Button>
            <div className="flex items-center gap-2">
              <Users className="size-5 text-blue-400" />
              <h1 className="text-xl font-semibold">User Management</h1>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-400">{currentUsername}</span>
            <Button variant="ghost" size="sm" onClick={onLogout} className="text-gray-400 hover:text-white">
              <LogOut className="size-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Toolbar */}
      <div className="px-6 py-4 flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-500" />
          <Input
            placeholder="Search by User ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-gray-900 border-gray-700 text-white"
          />
        </div>
        <Button onClick={() => setCreateDialogOpen(true)} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="size-4 mr-2" /> Create User
        </Button>
      </div>

      {/* Table */}
      <div className="px-6">
        <div className="rounded-lg border border-gray-800 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-gray-800 hover:bg-transparent">
                <TableHead className="text-gray-400">User ID</TableHead>
                <TableHead className="text-gray-400">Status</TableHead>
                <TableHead className="text-gray-400">Assigned Stations</TableHead>
                <TableHead className="text-gray-400">Created</TableHead>
                <TableHead className="text-gray-400 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-gray-500 py-8">Loading...</TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-gray-500 py-8">No users found</TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.id} className="border-gray-800">
                    <TableCell className="font-mono text-sm">{user.userid}</TableCell>
                    <TableCell>
                      <Badge variant={user.isAssigned ? 'default' : 'secondary'}
                        className={user.isAssigned ? 'bg-green-600/20 text-green-400 border-green-600/30' : 'bg-gray-600/20 text-gray-400 border-gray-600/30'}>
                        {user.isAssigned ? 'Assigned' : 'Unassigned'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1.5">
                        {user.subscriptions.length === 0 ? (
                          <span className="text-gray-500 text-sm">None</span>
                        ) : (
                          user.subscriptions.map((sub) => (
                            <Badge key={sub.id} variant="outline" className="border-blue-600/30 text-blue-400 gap-1">
                              <Radio className="size-3" />
                              {sub.stationName}
                              <button
                                onClick={() => setRevokeConfirm({ userId: user.id, subscriptionId: sub.id, stationName: sub.stationName })}
                                className="ml-1 hover:text-red-400 transition-colors"
                              >
                                <Trash2 className="size-3" />
                              </button>
                            </Badge>
                          ))
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-gray-400">
                      {new Date(user.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-blue-400 hover:text-blue-300"
                          onClick={() => { setAssignUserId(user.id); setAssignDialogOpen(true); }}
                        >
                          <UserPlus className="size-4 mr-1" /> Assign
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-400 hover:text-red-300"
                          onClick={() => setDeleteConfirm({ userId: user.id, userid: user.userid })}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Create User Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle>Create User</DialogTitle>
            <DialogDescription className="text-gray-400">
              Enter an 8-character User ID to create a new user.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              placeholder="e.g. USR12345"
              value={newUserId}
              onChange={(e) => setNewUserId(e.target.value.slice(0, 8))}
              maxLength={8}
              className="bg-gray-800 border-gray-600 text-white font-mono"
            />
            <p className="text-xs text-gray-500 mt-2">{newUserId.length}/8 characters</p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setCreateDialogOpen(false); setNewUserId(''); }}>Cancel</Button>
            <Button onClick={handleCreate} disabled={newUserId.length !== 8} className="bg-blue-600 hover:bg-blue-700">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Station Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={(open) => { setAssignDialogOpen(open); if (!open) { setAssignUserId(null); setSelectedStationId(''); } }}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle>Assign Station</DialogTitle>
            <DialogDescription className="text-gray-400">
              Select a station to give this user access to.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Select value={selectedStationId} onValueChange={setSelectedStationId}>
              <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                <SelectValue placeholder="Select a station" />
              </SelectTrigger>
              <SelectContent className="bg-gray-800 border-gray-600">
                {activeStations.map((station: any) => (
                  <SelectItem key={station.id} value={String(station.id)} className="text-white">
                    {station.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setAssignDialogOpen(false); setAssignUserId(null); setSelectedStationId(''); }}>Cancel</Button>
            <Button onClick={handleAssign} disabled={!selectedStationId} className="bg-blue-600 hover:bg-blue-700">Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => { if (!open) setDeleteConfirm(null); }}>
        <AlertDialogContent className="bg-gray-900 border-gray-700 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              Are you sure you want to delete user <span className="font-mono text-white">{deleteConfirm?.userid}</span>? This will also remove all their station subscriptions. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-gray-800 border-gray-600 text-white hover:bg-gray-700">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Revoke Confirm */}
      <AlertDialog open={!!revokeConfirm} onOpenChange={(open) => { if (!open) setRevokeConfirm(null); }}>
        <AlertDialogContent className="bg-gray-900 border-gray-700 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke Access</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              Remove access to <span className="text-white font-semibold">{revokeConfirm?.stationName}</span> for this user?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-gray-800 border-gray-600 text-white hover:bg-gray-700">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRevoke} className="bg-red-600 hover:bg-red-700">Revoke</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
